import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { User } from '../../types';
import { getGraphQLClient, SIGN_UP, GOOGLE_SIGN_IN } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { Button, Input } from '../common';

export default function RegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!email.trim()) {
      newErrors.email = t('errors.required');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('errors.invalidEmail');
    }

    if (!password) {
      newErrors.password = t('errors.required');
    } else if (password.length < 6) {
      newErrors.password = t('errors.passwordTooShort');
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('errors.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsLoading(true);

    try {
      const client = getGraphQLClient();
      console.log('Making GraphQL request to sign up...');
      const data = await client.request<{
        signUp: {
          token: string;
          user: User;
          errors: string[];
        };
      }>(SIGN_UP, { email, password });

      console.log('GraphQL response:', data);

      if (data.signUp.errors.length > 0) {
        setErrors({ form: data.signUp.errors[0] });
      } else {
        login(data.signUp.token, data.signUp.user);
        navigate('/');
      }
    } catch (error) {
      captureException(error, {
        component: 'RegisterForm',
        action: 'signUp',
        email,
      });
      setErrors({ form: t('common.error') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async (credential: string) => {
    setIsLoading(true);
    setErrors({});

    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        googleSignIn: {
          token: string;
          user: User;
          errors: string[];
        };
      }>(GOOGLE_SIGN_IN, { idToken: credential });

      if (data.googleSignIn.errors.length > 0) {
        setErrors({ form: data.googleSignIn.errors[0] });
      } else {
        login(data.googleSignIn.token, data.googleSignIn.user);
        navigate('/');
      }
    } catch (error) {
      captureException(error, {
        component: 'RegisterForm',
        action: 'googleSignIn',
      });
      setErrors({ form: t('common.error') });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google) {
        const googleClientId =
          document.querySelector('meta[name="google-client-id"]')?.getAttribute('content') || '';
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => handleGoogleSignIn(response.credential),
        });

        const buttonDiv = document.getElementById('google-signin-button');
        if (buttonDiv) {
          window.google.accounts.id.renderButton(buttonDiv, {
            theme: 'outline',
            size: 'large',
            width: 300,
          });
        }
      }
    };

    if (window.google) {
      initializeGoogleSignIn();
    } else {
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogleSignIn();
        }
      }, 100);

      return () => clearInterval(checkGoogle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md"
    >
      <div className="card p-8">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-gradient mb-2 hover:opacity-80 transition-opacity">
              {t('app.name')}
            </h1>
          </Link>
          <p className="text-gray-600 dark:text-gray-400">{t('auth.register')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            label={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            error={errors.email}
            required
          />

          <Input
            type="password"
            label={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            error={errors.password}
            required
          />

          <Input
            type="password"
            label={t('auth.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            error={errors.confirmPassword}
            required
          />

          {errors.form && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-red-500 text-center"
            >
              {errors.form}
            </motion.p>
          )}

          <Button type="submit" className="w-full" isLoading={isLoading}>
            {t('auth.register')}
          </Button>
        </form>

        <div className="my-6 flex items-center">
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
          <span className="px-4 text-sm text-gray-500">{t('auth.or')}</span>
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
        </div>

        <div id="google-signin-button" className="flex justify-center" />

        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            {t('auth.login')}
          </Link>
        </p>
      </div>
    </motion.div>
  );
}
